import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight, Phone, Mail, User as UserIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiErrorMessage } from '../lib/apiError';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SpecialistModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', interest: 'personal', message: '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Name and email are required');
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.message) delete payload.message;
      await axios.post(`${API}/leads`, payload);
      toast.success('Thanks. A specialist will be in touch shortly.');
      setForm({ name: '', email: '', phone: '', interest: 'personal', message: '' });
      onOpenChange(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not send request'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] bg-[#F5F1E8] border-[#0B3B2E]/15 p-0 overflow-hidden">
        <div className="p-7 pb-2">
          <DialogTitle asChild>
            <h2 className="font-serif text-[26px] leading-tight text-[#0B3B2E]">Talk to a specialist</h2>
          </DialogTitle>
          <DialogDescription className="text-[#0E1F1A]/60 mt-1 text-[13px]">
            Tell us the basics and a Vector FX specialist will call you back within one business day.
          </DialogDescription>
        </div>
        <form onSubmit={submit} className="px-7 pb-7 space-y-3">
          <Row label="Full name" icon={<UserIcon className="w-4 h-4" />}>
            <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Alex Morgan" className="pl-9" />
          </Row>
          <Row label="Email" icon={<Mail className="w-4 h-4" />}>
            <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="you@company.com" className="pl-9" />
          </Row>
          <Row label="Phone (optional)" icon={<Phone className="w-4 h-4" />}>
            <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44 20 …" className="pl-9" />
          </Row>
          <div className="space-y-1">
            <Label className="text-[12px] text-[#0E1F1A]/65">What are you looking to do?</Label>
            <select value={form.interest} onChange={(e) => set('interest', e.target.value)}
              className="h-11 w-full rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
              <option value="property">Buy or sell property abroad</option>
              <option value="business">Business FX or payroll</option>
              <option value="personal">Personal transfer</option>
              <option value="other">Something else</option>
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-[12px] text-[#0E1F1A]/65">Anything we should know (optional)</Label>
            <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={3}
              placeholder="e.g. Buying a villa in Portugal, moving £350k in the next month."
              className="w-full rounded-md border border-[#0B3B2E]/20 bg-white p-2.5 text-[14px]" />
          </div>
          <Button type="submit" disabled={busy}
            className="w-full rounded-full h-11 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] font-medium disabled:opacity-70">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending</>
              : <>Request a call <ArrowUpRight className="w-4 h-4 ml-1" /></>}
          </Button>
          <p className="text-center text-[11px] text-[#0E1F1A]/55">
            By submitting you agree to our Privacy Notice. We only use your details to reply.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, icon, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B3B2E]/50">{icon}</span>
        {children}
      </div>
    </div>
  );
}
